export enum ItemState {
  unknown = 'unknown',
  lock = 'lock',
  optional = 'optional',
  mandatory = 'mandatory',
}


export interface Rule<T, Context> {
  predicate: (paras: T) => [boolean, Context];
  keys: Set<string>;
}

interface ExclusionRuleParameters {
  selectedKeys: Set<string>;
}

interface ExclusionRuleContext {
  triggerColumnKeys: string[];
}

/**
 * As long as `predicate` is true, all column whose key is in `exclusionKeys` will be locked.
 */
export type ExclusionRule = Rule<ExclusionRuleParameters, ExclusionRuleContext>;

export enum ItemKind {
  candidate = 'candidate',
  selected = 'selected',
}

interface ItemBase {
  /**
   * a unique key for the item
   */
  key: string;
  /**
   * hunman-readable, translated name
   */
  friendlyName: string;
  state: ItemState;
  kind: ItemKind;
}

export interface CandidateItem extends ItemBase {
  helpTopic?: string;
  isSelected: boolean;
  kind: ItemKind.candidate;
}

export interface LockedCandidateItem extends CandidateItem {
  state: ItemState.lock;
  /**
   * translated text
   */
  // eslint-disable-next-line no-undef
  lockReason: string | JSX.Element;
}

export function isLockedCandidateItem(item: ItemBase): item is LockedCandidateItem {
  return (item.kind === ItemKind.candidate && item.state === ItemState.lock);
}

export interface SelectedItem extends ItemBase {
  kind: ItemKind.selected;
}

export type Item = CandidateItem | SelectedItem;

export type DraggingSelectedItemData = SelectedItem & {
  /**
   * origional index of dragging item in the original list
   */
  index: number;
  /**
   * current index of dragging item in the original list
   */
  hoverIndex?: number;
}

/**
 * Group data and some extra info that describes the state of a group of UI.
 * This is a little unclear, but it is more easier to use a flatten structure rather than deep properties.
 */
export interface GroupDescriptor {
  key: string;
  items: CandidateItem[];
  name: string;
  isCollapsed: boolean;
}

export interface CategoryDescriptor {
  key: string;
  headerName: string;
  groups: GroupDescriptor[];
}
